import { useRuntimeVariables } from 'app-kit'
import { Button } from 'designSystem/components/Button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from 'designSystem/components/Dialog'
import { Spinner } from 'designSystem/components/Spinner'
import { Text } from 'designSystem/components/Text'
import { Textarea } from 'designSystem/components/Textarea'
import { useGeckoAuth } from 'features/auth'
import {
  buildViewerBugReportSlackPayload,
  buildViewerBugReportUserBody,
  type ViewerBugReportContext,
} from 'features/knowledgeDocumentViewer/utils/viewerBugReport'
import { Flag } from 'lucide-react'
import { type ReactNode, useCallback, useState } from 'react'
import styled from 'styled-components'

type DocumentsBugReportButtonProps = {
  buildContext: (action: string) => ViewerBugReportContext
  onRecordAction?: (action: string) => void
  onSubmitSuccess?: () => void
  openActionLabel?: string
  renderTrigger?: (props: {
    disabled: boolean
    onClick: () => void
  }) => ReactNode
}

const BugReportDialogHeader = styled.header`
  align-items: flex-start;
  border-bottom: 0.0625rem solid var(--border);
  display: flex;
  gap: 1rem;
  padding: 1.5rem;
`

const BugReportDialogIcon = styled.div`
  align-items: center;
  background: var(--muted);
  border-radius: 0.5rem;
  color: var(--foreground);
  display: flex;
  flex: 0 0 auto;
  height: 2.5rem;
  justify-content: center;
  width: 2.5rem;

  svg {
    height: 1.25rem;
    width: 1.25rem;
  }
`

const BugReportDialogTitleGroup = styled.div`
  display: grid;
  gap: 0.375rem;
  padding-right: 2rem;
`

const BugReportDialogBody = styled.div`
  display: grid;
  gap: 1rem;
  padding: 1.5rem;
`

const TextareaContainer = styled.div`
  textarea {
    min-height: 10rem;
    resize: vertical;
    line-height: 1.45;
  }
`

const BugReportDialogFooter = styled.footer`
  border-top: 0.0625rem solid var(--border);
  display: flex;
  gap: 0.75rem;
  padding: 1.5rem;

  > button {
    flex: 1 1 0;
    min-width: 0;
  }
`

export function DocumentsBugReportButton({
  buildContext,
  onRecordAction,
  onSubmitSuccess,
  openActionLabel = 'Opened bug report from documents page',
  renderTrigger,
}: DocumentsBugReportButtonProps) {
  const { FORECAST_VERSION: forecastVersion, POWER_BACKEND_URL } =
    useRuntimeVariables()
  const { authorizedAxios } = useGeckoAuth()

  const [isOpen, setIsOpen] = useState(false)
  const [context, setContext] = useState<ViewerBugReportContext | null>(null)
  const [body, setBody] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleOpen = useCallback(() => {
    onRecordAction?.(openActionLabel)
    setContext({
      ...buildContext(openActionLabel),
      forecastVersion,
    })
    setBody(buildViewerBugReportUserBody())
    setError(null)
    setIsOpen(true)
  }, [buildContext, forecastVersion, onRecordAction, openActionLabel])

  const handleSubmit = useCallback(async () => {
    if (!context) return

    setIsSubmitting(true)
    setError(null)

    try {
      await authorizedAxios.post(
        `${POWER_BACKEND_URL}/api/v2/document-viewer/bug-reports/slack`,
        buildViewerBugReportSlackPayload(context, body),
        { headers: { 'Content-Type': 'application/json' } }
      )
      setIsOpen(false)
      onSubmitSuccess?.()
    } catch (submitError) {
      console.error('Failed to send document viewer bug report', submitError)
      setError('Could not send the issue report. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }, [POWER_BACKEND_URL, authorizedAxios, body, context, onSubmitSuccess])

  return (
    <>
      {renderTrigger ? (
        renderTrigger({ disabled: isSubmitting, onClick: handleOpen })
      ) : (
        <Button
          variant='inputRaised'
          size='toolbar'
          onClick={handleOpen}
          disabled={isSubmitting}
          title='Report Issue'>
          <Flag size={18} strokeWidth={1.5} />
          <span>Report Issue</span>
        </Button>
      )}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent size='md' padding='none'>
          <BugReportDialogHeader>
            <BugReportDialogIcon>
              <Flag />
            </BugReportDialogIcon>
            <BugReportDialogTitleGroup>
              <DialogTitle size='sm'>Report Issue</DialogTitle>
              <DialogDescription size='sm'>
                Send a message to the Gecko Support Team.
              </DialogDescription>
            </BugReportDialogTitleGroup>
          </BugReportDialogHeader>
          <BugReportDialogBody>
            <TextareaContainer>
              <Textarea
                value={body}
                onChange={(event) => setBody(event.target.value)}
                disabled={isSubmitting}
                aria-label='Issue report details'
                placeholder='Tell us what went wrong...'
              />
            </TextareaContainer>
            {error ? (
              <Text as='p' size='sm' color='destructive'>
                {error}
              </Text>
            ) : null}
          </BugReportDialogBody>
          <BugReportDialogFooter>
            <Button
              type='button'
              variant='inputRaised'
              size='lg'
              onClick={() => setIsOpen(false)}
              disabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              type='button'
              size='lg'
              onClick={handleSubmit}
              disabled={isSubmitting || body.trim().length === 0}>
              {isSubmitting && <Spinner size={16} />}
              {isSubmitting ? 'Submitting...' : 'Submit'}
            </Button>
          </BugReportDialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
